import React from 'react';
import { motion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#') || href.startsWith('#')) {
      const hash = href.startsWith('/#') ? href.substring(2) : href.substring(1);
      const isOnLandingPage = location.pathname === '/' || location.pathname === '/landing';

      if (isOnLandingPage) {
        e.preventDefault();
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        e.preventDefault();
        navigate('/#' + hash);
      }
    }
  };

  const footerLinks = {
    product: [
      { name: 'Features', href: '/#features' },
      { name: 'Pricing', href: '/pages-pricing' },
      { name: 'How It Works', href: '/#how-it-works' },
      { name: 'AI Technology', href: '/#ai-features' },
      // SEO: /tools/* had no site-wide link — the navbar Tools dropdown is
      // the only other entry point and it is CSS-hidden until opened.
      { name: 'Profit Calculator', href: '/tools/profit-calculator' },
      { name: 'eBay Fee Calculator', href: '/tools/ebay-fee-calculator' },
      { name: 'Title Optimizer', href: '/tools/title-optimizer' }
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Contact', href: 'mailto:support@coderom.com' },
      { name: 'Blog', href: '/blog' },
      { name: 'Community', href: '/community' },
      { name: 'vs AutoDS', href: '/compare/coderom-vs-autods' },
      { name: 'vs Easync', href: '/compare/coderom-vs-easync' },
      { name: 'vs Yaballe', href: '/compare/coderom-vs-yaballe' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' },
      { name: 'GDPR', href: '/gdpr' }
    ],
    support: [
      { name: 'Help Centre', href: '/help' },
      { name: 'FAQ', href: '/faq' },
      // SEO: 'Documentation' pointed at /help as well — a duplicate link that
      // wasted a footer slot. Replaced with real help articles, which had no
      // site-wide link of their own.
      { name: 'Getting Started Guide', href: '/help/getting-started' },
      { name: 'AI Listing Guide', href: '/help/ai-listing' },
      { name: 'Repricing Guide', href: '/help/pricing' },
      { name: 'Contact Support', href: 'mailto:support@coderom.com' }
    ]
  };

  return (
    <footer className="site-footer">
      <div className="footer-top">
        <Container>
          <Row className="g-4">
            {/* Brand Column */}
            <Col lg={4} md={6}>
              <motion.div
                className="footer-brand"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Link to="/" className="brand-logo" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                  <img
                    src="/logo-full-white.png"
                    alt="CodeRom"
                    loading="lazy"
                    style={{ height: '32px', width: 'auto' }}
                  />
                </Link>
                <p className="brand-description">
                  AI-powered Amazon to eBay automation platform. Listing creation,
                  smart repricing, automatic ordering, tracking, and buyer messaging —
                  everything you need to run and scale your dropshipping business.
                </p>

                {/* Social Links */}
                <div className="social-links">
                  <a href="https://twitter.com/coderomapp" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Twitter">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </a>
                  <a href="https://facebook.com/coderomapp" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a href="https://linkedin.com/company/coderom" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                  <a href="https://youtube.com/@coderomapp" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="YouTube">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </a>
                </div>
              </motion.div>
            </Col>

            {/* Links Columns */}
            <Col lg={2} md={3} xs={6}>
              <motion.div
                className="footer-links-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h6 className="footer-title">Product</h6>
                <ul className="footer-links">
                  {footerLinks.product.map((link, index) => (
                    <li key={index}>
                      <a href={link.href} onClick={(e) => handleHashClick(e, link.href)}>{link.name}</a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </Col>

            <Col lg={2} md={3} xs={6}>
              <motion.div
                className="footer-links-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <h6 className="footer-title">Company</h6>
                <ul className="footer-links">
                  {footerLinks.company.map((link, index) => (
                    <li key={index}>
                      <a href={link.href} onClick={(e) => handleHashClick(e, link.href)}>{link.name}</a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </Col>

            <Col lg={2} md={3} xs={6}>
              <motion.div
                className="footer-links-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h6 className="footer-title">Support</h6>
                <ul className="footer-links">
                  {footerLinks.support.map((link, index) => (
                    <li key={index}>
                      <a href={link.href} onClick={(e) => handleHashClick(e, link.href)}>{link.name}</a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </Col>

            <Col lg={2} md={3} xs={6}>
              <motion.div
                className="footer-links-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.25 }}
              >
                <h6 className="footer-title">Legal</h6>
                <ul className="footer-links">
                  {footerLinks.legal.map((link, index) => (
                    <li key={index}>
                      <a href={link.href} onClick={(e) => handleHashClick(e, link.href)}>{link.name}</a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <Container>
          <div className="footer-bottom-content">
            <p className="copyright">
              © {currentYear} CodeRom. All rights reserved.
            </p>
            <div className="footer-meta">
              <span className="location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                United Kingdom
              </span>
              <span className="divider">|</span>
              <a href="mailto:support@coderom.com" className="email">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                support@coderom.com
              </a>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
